<!--
  Окно выбранного разговора: показывает сообщения и держит локальное состояние ввода.
-->
<template>
  <div class="conversation-container">
    <div v-if="!conversationId" class="conversation-placeholder">
      <div class="placeholder-content">
        <AppIcon class="placeholder-icon" name="comment" :size="40" />
        <p>Выберите чат, чтобы просмотреть историю сообщений</p>
      </div>
    </div>
    <div v-else class="conversation-block">
      <div class="conversation-header">
        <div class="header-user">
          <button
            v-if="showBackButton"
            type="button"
            class="conversation-back-button"
            title="Вернуться к списку чатов"
            @click="$emit('back')"
          >
            <AppIcon name="chevronLeft" :size="20" />
          </button>
          <div class="user-avatar">
            <img
              v-if="conversationAvatarSrc"
              :src="conversationAvatarSrc"
              :alt="conversationName || `Чат ${conversationId}`"
              class="avatar-image"
            />
            <div
              v-else
              class="avatar-gradient"
              :style="
                getAvatarStyle(conversationName || conversationId)
              "
            >
              {{
                getAvatarInitials(
                  conversationName || `Чат ${conversationId}`
                )
              }}
            </div>
          </div>
          <h3>{{ conversationName || `Чат #${conversationId}` }}</h3>
        </div>
      </div>
      <div
        class="messages-block"
        ref="messagesContainer"
        @scroll="handleMessagesScroll"
      >
        <div
          v-if="chatStore.olderMessagesAreLoading"
          class="messages-loader"
        >
          Загрузка сообщений...
        </div>
        <template v-for="item in messageFeedItems" :key="item.key">
          <ChatDateSeparator
            v-if="item.type === 'date'"
            :label="item.label"
            :date-key="item.dateKey"
          />
          <ChatMessageItem
            v-else
            :message="item.message"
            :conversation-name="conversationName"
            :retrying="messageRetryIsLoading(item.message)"
            @retry="retryMessage"
          />
        </template>
      </div>
      <div class="message-input-container">
        <div
          v-if="pendingAttachments.length"
          class="pending-attachments"
        >
          <div
            v-for="attachment in pendingAttachments"
            :key="attachment.id"
            class="pending-attachment"
          >
            <img
              v-if="isImageAttachment(attachment)"
              :src="attachmentLink(attachment)"
              :alt="attachment.name || 'Вложение'"
              class="pending-attachment__image"
            />
            <span class="pending-attachment__name">
              {{ attachment.name || "Вложение" }}
            </span>
            <span class="pending-attachment__size">
              {{ formatAttachmentSize(attachment) }}
            </span>
            <button
              type="button"
              class="pending-attachment__remove"
              title="Убрать файл"
              @click="removePendingAttachment(attachment)"
            >
              <AppIcon name="close" :size="14" />
            </button>
          </div>
        </div>
        <div class="message-input">
          <input
            :id="fileInputId"
            type="file"
            multiple
            class="message-file-input"
            @change="handleFileUpload"
          />
          <input
            :id="cameraInputId"
            type="file"
            accept="image/*"
            capture="environment"
            class="message-file-input"
            @change="handleFileUpload"
          />
          <input
            :id="galleryInputId"
            type="file"
            accept="image/*"
            multiple
            class="message-file-input"
            @change="handleFileUpload"
          />
          <input
            :id="mobileFileInputId"
            type="file"
            multiple
            class="message-file-input"
            @change="handleFileUpload"
          />
          <div class="message-attach">
            <button
              type="button"
              class="message-icon-button"
              title="Прикрепить файл"
              :disabled="attachmentUploadIsLoading"
              @click="toggleAttachmentMenu"
            >
              <AppIcon name="paperclip" :size="18" />
            </button>
            <div
              v-if="attachmentMenuOpen"
              class="message-attach-menu"
            >
              <label
                :for="fileInputId"
                class="message-attach-action message-attach-action--desktop"
              >
                <AppIcon name="paperclip" :size="16" />
                Выбрать файл
              </label>
              <label
                :for="cameraInputId"
                class="message-attach-action message-attach-action--mobile"
              >
                <AppIcon name="camera" :size="16" />
                Снять фото
              </label>
              <label
                :for="galleryInputId"
                class="message-attach-action message-attach-action--mobile"
              >
                <AppIcon name="image" :size="16" />
                Выбрать фото
              </label>
              <label
                :for="mobileFileInputId"
                class="message-attach-action message-attach-action--mobile"
              >
                <AppIcon name="paperclip" :size="16" />
                Выбрать файл
              </label>
            </div>
          </div>
          <input
            v-model="newMessage"
            @keyup.enter="sendMessage"
            type="text"
            placeholder="Напишите сообщение..."
            class="message-field"
          />
          <button
            @click="sendMessage"
            class="send-button"
            :disabled="sendButtonDisabled"
            title="Отправить"
            aria-label="Отправить"
          >
            <AppIcon
              class="send-button__icon"
              name="send"
              :size="18"
            />
            <span class="send-button__text">Отправить</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { useNotificationStore } from "@/stores/notificationStore";
import { useChatStore } from "@/stores/chatStore";
import AppIcon from "@/components/Icon/AppIcon.vue";
import ChatDateSeparator from "@/components/Chat/DateSeparator.vue";
import ChatMessageItem from "@/components/Chat/MessageItem.vue";
import { getAvatarStyle, getAvatarInitials } from "@/utils/chatUtils";
import {
  attachmentUrl,
  attachmentSize,
  formatFileSize,
  isImage,
  isFileTooLarge,
  resolveUrl,
} from "@/utils/fileUtils";
import { shouldScrollChatToBottom } from "@/utils/chatScrollUtils";

export default {
  name: "ConversationWindow",
  components: {
    AppIcon,
    ChatDateSeparator,
    ChatMessageItem,
  },
  props: {
    conversationId: {
      type: Number,
      default: null,
    },
    conversationName: {
      type: String,
      default: "",
    },
    conversationAvatarUrl: {
      type: String,
      default: "",
    },
    showBackButton: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["back"],
  data() {
    return {
      newMessage: "",
      pendingAttachments: [],
      attachmentUploadIsLoading: false,
      attachmentMenuOpen: false,
      forceScrollToBottom: false,
      preserveMessagesScroll: false,
    };
  },
  watch: {
    conversationId(newId) {
      if (!newId) {
        this.newMessage = "";
        this.pendingAttachments = [];
        this.attachmentMenuOpen = false;
      }

      this.forceScrollToBottom = true;
    },

    messages: {
      handler() {
        if (this.preserveMessagesScroll) return;

        const container = this.$refs.messagesContainer;
        const shouldScroll =
          this.forceScrollToBottom ||
          shouldScrollChatToBottom(container);
        this.forceScrollToBottom = false;
        if (!shouldScroll) return;

        // Статус доставки и realtime могут обновить уже видимое сообщение.
        // Вниз скроллим только если пользователь и так был у низа чата
        this.$nextTick(() => this.scrollToBottom());
      },
      deep: true,
    },
  },
  computed: {
    chatStore() {
      return useChatStore();
    },

    notificationStore() {
      return useNotificationStore();
    },

    fileInputId() {
      return `chat-file-upload-${this.conversationId}`;
    },

    cameraInputId() {
      return `${this.fileInputId}-camera`;
    },

    galleryInputId() {
      return `${this.fileInputId}-gallery`;
    },

    mobileFileInputId() {
      return `${this.fileInputId}-mobile-file`;
    },

    conversationAvatarSrc() {
      return resolveUrl(this.conversationAvatarUrl);
    },

    messages() {
      return this.chatStore.messages;
    },

    messageFeedItems() {
      const items = [];
      let previousDateKey = null;

      this.messages.forEach((message) => {
        const dateKey = this.messageDateKey(message);
        if (dateKey && dateKey !== previousDateKey) {
          items.push({
            type: "date",
            key: `date-${dateKey}`,
            label: this.messageDateLabel(message),
            dateKey,
          });
          previousDateKey = dateKey;
        }

        items.push({
          type: "message",
          key: `message-${message.id}`,
          message,
        });
      });

      return items;
    },

    sendButtonDisabled() {
      return (
        this.attachmentUploadIsLoading || !this.messagePayloadPresent
      );
    },

    messagePayloadPresent() {
      return Boolean(
        this.newMessage.trim() || this.pendingAttachments.length
      );
    },
  },
  methods: {
    async sendMessage() {
      if (this.sendButtonDisabled) return;

      const payload = {
        text: this.newMessage,
        attachment_ids: this.pendingAttachments.map(
          (item) => item.id
        ),
      };
      this.forceScrollToBottom = true;
      await this.chatStore.sendMessage(payload);
      this.newMessage = "";
      this.pendingAttachments = [];
    },

    retryMessage(message) {
      this.chatStore.retryMessage(message);
    },

    async handleMessagesScroll() {
      const container = this.$refs.messagesContainer;
      if (
        !container ||
        container.scrollTop > 24 ||
        !this.chatStore.olderMessagesAreAvailable
      ) {
        return;
      }

      await this.loadOlderMessages();
    },

    async loadOlderMessages() {
      const container = this.$refs.messagesContainer;
      if (!container) return;

      const previousScrollHeight = container.scrollHeight;
      const previousScrollTop = container.scrollTop;

      this.preserveMessagesScroll = true;
      try {
        await this.chatStore.loadOlderMessages();
        await this.$nextTick();
        container.scrollTop =
          container.scrollHeight -
          previousScrollHeight +
          previousScrollTop;
      } finally {
        this.preserveMessagesScroll = false;
      }
    },

    messageRetryIsLoading(message) {
      return Boolean(this.chatStore.retryingMessageIds[message.id]);
    },

    messageDateKey(message) {
      const date = new Date(message.created_at);
      if (Number.isNaN(date.getTime())) return "";

      return [
        date.getFullYear(),
        String(date.getMonth() + 1).padStart(2, "0"),
        String(date.getDate()).padStart(2, "0"),
      ].join("-");
    },

    messageDateLabel(message) {
      const date = new Date(message.created_at);
      if (Number.isNaN(date.getTime())) return "";

      const today = new Date();
      const yesterday = new Date(today);
      yesterday.setDate(today.getDate() - 1);
      const tomorrow = new Date(today);
      tomorrow.setDate(today.getDate() + 1);

      const dateKey = this.messageDateKey(message);
      if (dateKey === this.dateKey(today)) return "Сегодня";
      if (dateKey === this.dateKey(yesterday)) return "Вчера";
      if (dateKey === this.dateKey(tomorrow)) return "Завтра";

      return date.toLocaleDateString("ru-RU", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });
    },

    dateKey(date) {
      return [
        date.getFullYear(),
        String(date.getMonth() + 1).padStart(2, "0"),
        String(date.getDate()).padStart(2, "0"),
      ].join("-");
    },

    toggleAttachmentMenu() {
      this.attachmentMenuOpen = !this.attachmentMenuOpen;
    },

    async handleFileUpload(event) {
      const files = Array.from(event.target.files || []);
      event.target.value = "";
      if (!files.length) return;

      this.attachmentMenuOpen = false;
      this.attachmentUploadIsLoading = true;
      try {
        for (const file of files) {
          await this.uploadAttachment(file);
        }
      } finally {
        this.attachmentUploadIsLoading = false;
      }
    },

    async uploadAttachment(file) {
      if (isFileTooLarge(file)) {
        this.notificationStore.showError(
          `Файл ${file.name} слишком большой`
        );
        return;
      }

      const attachment = await this.chatStore.uploadAttachment(file);
      this.pendingAttachments.push(attachment);
    },

    async removePendingAttachment(attachment) {
      await this.chatStore.deleteAttachment(attachment.id);
      this.pendingAttachments = this.pendingAttachments.filter(
        (item) => item.id !== attachment.id
      );
    },

    scrollToBottom() {
      const container = this.$refs.messagesContainer;
      if (container) {
        container.scrollTop = container.scrollHeight;
      }
    },

    isImageAttachment(attachment) {
      return isImage(attachment);
    },

    attachmentLink(attachment) {
      return resolveUrl(attachmentUrl(attachment));
    },

    formatAttachmentSize(attachment) {
      return formatFileSize(attachmentSize(attachment));
    },

    getAvatarStyle,
    getAvatarInitials,
  },
};
</script>

<style scoped>
.conversation-container {
  height: 100%;
  width: 100%;
  min-height: 0;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.conversation-placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  padding: 40px 20px;
  background: #fafafa;
}

.placeholder-content {
  text-align: center;
  color: #999;
}

.placeholder-icon {
  font-size: 40px;
  margin-bottom: 16px;
  opacity: 0.5;
}

.placeholder-content p {
  font-size: 14px;
  margin: 0;
}

.conversation-block {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
}

.conversation-header {
  padding: 16px 20px;
  background: #fff;
  border-bottom: 1px solid #eaeaea;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  flex-shrink: 0;
}

.header-user {
  display: flex;
  align-items: center;
  gap: 12px;
}

.conversation-back-button {
  width: 36px;
  height: 36px;
  border: none;
  border-radius: 50%;
  background: #f3f4f6;
  color: #333;
  cursor: pointer;
  display: none;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: background-color 0.2s ease, color 0.2s ease;
}

.conversation-back-button:hover {
  background: #e5e7eb;
  color: var(--color-green);
}

@media (max-width: 768px) {
  .conversation-back-button {
    display: inline-flex;
  }
}

.user-avatar {
  flex-shrink: 0;
}

.avatar-gradient {
  width: 35px;
  height: 35px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 600;
  font-size: 16px;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.avatar-image {
  width: 35px;
  height: 35px;
  border-radius: 50%;
  object-fit: cover;
  display: block;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.avatar-gradient.small {
  width: 25px;
  height: 25px;
  font-size: 11px;
}

.conversation-header h3 {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #333;
}

.messages-block {
  flex: 1;
  min-height: 0;
  padding: 16px;
  overflow-y: auto;
  overflow-x: hidden;
  background: #f9f9f9;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.messages-loader {
  align-self: center;
  padding: 4px 10px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.82);
  color: #777;
  font-size: 11px;
  line-height: 1.2;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.08);
}

.message-input-container {
  padding: 16px;
  background: #fff;
  border-top: 1px solid #eaeaea;
  flex-shrink: 0;
}

.message-input {
  display: flex;
  gap: 12px;
  align-items: center;
}

.message-file-input {
  display: none;
}

.message-attach {
  position: relative;
  flex-shrink: 0;
}

.message-icon-button {
  width: 40px;
  height: 40px;
  border: 1px solid #d8d8d8;
  border-radius: 50%;
  background: #fff;
  color: #555;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
}

.message-icon-button:hover:not(:disabled) {
  border-color: var(--color-green);
  color: var(--color-green);
}

.message-icon-button:disabled {
  cursor: not-allowed;
  opacity: 0.55;
}

.message-attach-menu {
  position: absolute;
  left: 0;
  bottom: calc(100% + 8px);
  z-index: 5;
  min-width: 170px;
  padding: 6px;
  border: 1px solid #e4e4e4;
  border-radius: 10px;
  background: #fff;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.14);
}

.message-attach-action {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 9px 10px;
  border-radius: 8px;
  color: #333;
  cursor: pointer;
  font-size: 13px;
  white-space: nowrap;
}

.message-attach-action:hover {
  background: #f2f7f3;
  color: var(--color-green);
}

.message-attach-action--mobile {
  display: none;
}

.message-field {
  flex: 1;
  padding: 12px 16px;
  border: 1px solid #ddd;
  border-radius: 8px;
  outline: none;
  font-size: 14px;
  background: #fff;
}

.message-field:focus {
  border-color: var(--color-green);
}

.send-button {
  padding: 12px 20px;
  background: var(--color-green);
  color: #fff;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 500;
  font-size: 14px;
  white-space: nowrap;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 42px;
}

.send-button__icon {
  display: none;
}

.send-button:hover {
  background: var(--color-green-hover);
}

.send-button:disabled {
  cursor: not-allowed;
  opacity: 0.55;
}

.pending-attachments {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 10px;
}

.pending-attachment {
  display: inline-grid;
  grid-template-columns: auto minmax(0, 1fr) auto auto;
  align-items: center;
  gap: 8px;
  max-width: 100%;
  padding: 6px 8px;
  border: 1px solid #e0e0e0;
  border-radius: 10px;
  background: #fafafa;
  color: #333;
  font-size: 12px;
}

.pending-attachment__image {
  width: 30px;
  height: 30px;
  border-radius: 7px;
  object-fit: cover;
}

.pending-attachment__name {
  min-width: 0;
  max-width: 180px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.pending-attachment__size {
  color: #777;
  white-space: nowrap;
}

.pending-attachment__remove {
  width: 24px;
  height: 24px;
  border: 0;
  border-radius: 50%;
  background: transparent;
  color: #777;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.pending-attachment__remove:hover {
  background: #eeeeee;
  color: #333;
}

@media (max-width: 768px) {
  .conversation-container {
    border-radius: 10px;
  }

  .message-input {
    gap: 6px;
  }

  .message-input-container {
    padding: 10px;
  }

  .messages-block {
    padding: 10px;
  }

  .message-field {
    min-width: 0;
    padding: 10px 12px;
  }

  .send-button {
    width: 40px;
    min-width: 40px;
    height: 40px;
    min-height: 40px;
    padding: 0;
    border-radius: 50%;
    flex-shrink: 0;
  }

  .send-button__icon {
    display: inline-flex;
    transform: translateX(-1px);
  }

  .send-button__text {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
  }

  .message-attach-menu {
    min-width: 180px;
  }

  .message-attach-action--desktop {
    display: none;
  }

  .message-attach-action--mobile {
    display: flex;
  }

  .pending-attachment {
    width: 100%;
  }

  .pending-attachment__name {
    max-width: none;
  }
}
</style>
